import {
  Badge,
  Box,
  Flex,
  HStack,
  Spinner,
  Text,
  VStack,
  chakra,
} from "@chakra-ui/react"
import { Link as RouterLink, useMatch, useParams } from "react-router-dom"
import SectionCard from "../../framework/SectionCard"
import { useResort } from "../../hooks/useResort"
import ResortInfoTab from "./tabs/ResortInfoTab"
import ResortMapTab from "./tabs/ResortMapTab"
import ResortFeaturesTab from "./tabs/ResortFeaturesTab"
import ResortOpeningsTab from "./tabs/ResortOpeningsTab"
import ResortSnowReportsTab from "./tabs/ResortSnowReportsTab"
import ErrorAlert from "../../components/ErrorAlert"
import { extractErrorInfo } from "../../lib/api/error-utils"

type TabValue = "info" | "map" | "features" | "openings" | "snow-reports"

const StyledTabLink = chakra(RouterLink)

export default function ResortDetail() {
  const { uid } = useParams<{ uid: string }>()
  const resortUid = uid ?? ""
  const { data, isLoading, error } = useResort(uid)
  
  const mapMatch = useMatch("/resorts/:uid/map")
  const featuresMatch = useMatch({ path: "/resorts/:uid/features", end: true })
  const openingsMatch = useMatch({ path: "/resorts/:uid/openings", end: true })

  const snowMatch = useMatch({ path: "/resorts/:uid/snow-reports", end: true })

  const activeTab: TabValue = snowMatch ? "snow-reports" : mapMatch ? "map" : featuresMatch ? "features" : openingsMatch ? "openings" : "info"

  const errorInfo = error ? extractErrorInfo(error) : null

  return (
    <SectionCard>
      <Flex justify="space-between" align="center" mb="4" gap="3" flexWrap="wrap">
        <HStack gap="3">
          <HStack gap="2" align="center" color="gray.300" fontSize="sm">
            <StyledTabLink to="/resorts" color="blue.200">
              Resorts
            </StyledTabLink>
            <Text>/</Text>
            <Text>{data?.name ?? "Resort"}</Text>
          </HStack>
          {data?.isGroup && (
            <Badge colorPalette="purple" variant="solid">
              GROUP
            </Badge>
          )}
        </HStack>
        <Text fontSize="sm" color="gray.400">
          UID: <Box as="span" fontFamily="mono" color="gray.200">
            {data?.uid ?? uid}
          </Box>
        </Text>
      </Flex>

      {errorInfo && <ErrorAlert message={errorInfo.message} traceId={errorInfo.traceId} />}

      {isLoading ? (
        <Flex align="center" justify="center" py="10" gap="3">
          <Spinner size="md" />
          <Text>Loading resort...</Text>
        </Flex>
      ) : !data ? (
        <Text color="gray.400">No resort data found.</Text>
      ) : (
        <VStack align="stretch" gap="6">
          <HStack gap="1" borderBottomWidth="1px" borderColor="gray.700" pb="0">
            <StyledTabLink
              to={`/resorts/${resortUid}`}
              display="inline-flex"
              alignItems="center"
              px="4"
              py="2"
              fontSize="sm"
              fontWeight="medium"
              borderTopRadius="md"
              bg={activeTab === "info" ? "blue.600" : "transparent"}
              color={activeTab === "info" ? "white" : "gray.100"}
              _hover={{ bg: activeTab === "info" ? "blue.600" : "blue.700", color: "white", textDecoration: "none" }}
            >
              Info
            </StyledTabLink>
            <StyledTabLink
              to={`/resorts/${resortUid}/map`}
              display="inline-flex"
              alignItems="center"
              px="4"
              py="2"
              fontSize="sm"
              fontWeight="medium"
              borderTopRadius="md"
              bg={activeTab === "map" ? "blue.600" : "transparent"}
              color={activeTab === "map" ? "white" : "gray.100"}
              _hover={{ bg: activeTab === "map" ? "blue.600" : "blue.700", color: "white", textDecoration: "none" }}
            >
              Map
            </StyledTabLink>
            <StyledTabLink
              to={`/resorts/${resortUid}/features`}
              display="inline-flex"
              alignItems="center"
              px="4"
              py="2"
              fontSize="sm"
              fontWeight="medium"
              borderTopRadius="md"
              bg={activeTab === "features" ? "blue.600" : "transparent"}
              color={activeTab === "features" ? "white" : "gray.100"}
              _hover={{ bg: activeTab === "features" ? "blue.600" : "blue.700", color: "white", textDecoration: "none" }}
            >
              Features
            </StyledTabLink>
            <StyledTabLink
              to={`/resorts/${resortUid}/openings`}
              display="inline-flex"
              alignItems="center"
              px="4"
              py="2"
              fontSize="sm"
              fontWeight="medium"
              borderTopRadius="md"
              bg={activeTab === "openings" ? "blue.600" : "transparent"}
              color={activeTab === "openings" ? "white" : "gray.100"}
              _hover={{ bg: activeTab === "openings" ? "blue.600" : "blue.700", color: "white", textDecoration: "none" }}
            >
              Openings
            </StyledTabLink>
            <StyledTabLink
              to={`/resorts/${resortUid}/snow-reports`}
              display="inline-flex" alignItems="center" px="4" py="2"
              fontSize="sm" fontWeight="medium" borderTopRadius="md"
              bg={activeTab === "snow-reports" ? "blue.600" : "transparent"}
              color={activeTab === "snow-reports" ? "white" : "gray.100"}
              _hover={{ bg: "blue.700", color: "white", textDecoration: "none" }}
            >
              Snow Reports
            </StyledTabLink>
          </HStack>

          {activeTab === "info" && <ResortInfoTab data={data} />}
          {activeTab === "map" && <ResortMapTab convexHull={data.convexHull} geometry={data.geometry} />}
          {activeTab === "features" && <ResortFeaturesTab resortUid={resortUid} />}
          {activeTab === "snow-reports" && <ResortSnowReportsTab resortUid={resortUid} />}
          {activeTab === "openings" && <ResortOpeningsTab resortUid={resortUid} />}
        </VStack>
      )}
    </SectionCard>
  )
}
